import { styled } from '@mui/material';

import { useState } from 'react';

import { useNavigate } from 'react-router';

import { PageContent, Tooltip, Typography } from '@app-velocity/ui';
import { Loader } from '@app-velocity/animations';

import { useI18n } from '@app-velocity/i18n';

import { ArrowForwardIcon } from '@app-velocity/icons';

import { ViewPreview } from '../../components/ViewPreview';
import { usePathView } from './hooks/usePathView';
import { PathScreens } from '../../components/PathScreens';
import { Lightbox } from './components/Lightbox';
import { useReportInternalContex } from '../../context/useReportInternalContext';
import { useReportContext } from '../../context/useReportContext';
import { ReportPaths } from '../../paths/ReportPaths';

const ContentWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(6),
}));

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  width: '100%',
  justifyContent: 'space-around',
  flexWrap: 'wrap',
  position: 'relative',
}));

const StyledMainContent = styled('div')({
  flex: '0 0 auto',
});

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 700,
}));

const StyledSpacer = styled('span')({
  flex: '0 0 25%',
});

const ViewContainer = styled('div')({
  cursor: 'pointer',
});

const RightButton = styled('button')(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  right: 16,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: 'rgba(0,0,0,0.7)',
  width: theme.spacing(8),
  height: theme.spacing(8),
  svg: {
    fill: '#fff',
    width: 20,
    height: 20,
  },
  zIndex: 2,
}));

const LeftButton = styled('button')(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: 16,
  // background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',

  alignItems: 'center',
  justifyContent: 'center',
  transform: 'rotate(180deg)',
  borderRadius: '50%',
  background: 'rgba(0,0,0,0.7)',
  width: theme.spacing(8),
  height: theme.spacing(8),
  svg: {
    fill: '#fff',
    width: 20,
    height: 20,
  },
  zIndex: 2,
}));

export const PathView = () => {
  const { t } = useI18n();
  const data = usePathView();
  const navigate = useNavigate();

  const { renderPathDebug, baseURL } = useReportContext();
  const { featurePreview } = useReportInternalContex();
  const [lightbox, setLightbox] = useState(false);

  if (!data) return <Loader />;
  const { view, viewIndex, path, pathNumber } = data;

  const pathStatus =
    path.status === 'CANCELED'
      ? t('prototypes:report.paths.unsuccessful')
      : t('prototypes:report.paths.successful');

  const toggleLightbox = () => {
    setLightbox((prev) => !prev);
  };

  const handleNextScreen = () => {
    const nextView = path.views[viewIndex + 1];

    if (!nextView) return;

    navigate(
      ReportPaths.goToSinglePathView(
        baseURL,
        path.type,
        path.id,
        nextView.checksum
      )
    );
  };

  const handlePrevScreen = () => {
    const nextView = path.views[viewIndex - 1];

    if (!nextView) return;

    navigate(
      ReportPaths.goToSinglePathView(
        baseURL,
        path.type,
        path.id,
        nextView.checksum
      )
    );
  };

  return (
    <PageContent>
      {lightbox ? (
        <Lightbox viewIndex={viewIndex} path={path} onClose={toggleLightbox} />
      ) : null}
      <ContentWrapper>
        <header>
          <PageContent.Title
            title={`${t('prototypes:report.paths.number', {
              number: pathNumber,
            })} - ${pathStatus}`}
          />
        </header>
        <Container>
          {viewIndex < path.views.length - 1 ? (
            <RightButton onClick={handleNextScreen}>
              <ArrowForwardIcon />
            </RightButton>
          ) : null}
          {viewIndex > 0 ? (
            <LeftButton onClick={handlePrevScreen}>
              <ArrowForwardIcon />
            </LeftButton>
          ) : null}
          {featurePreview && renderPathDebug
            ? renderPathDebug(view, viewIndex, path, pathNumber)
            : null}
          <StyledMainContent>
            <Tooltip title={t('prototypes:report.paths.open_lightbox')}>
              <ViewContainer onClick={toggleLightbox}>
                <ViewPreview
                  persona={path.persona || 'default'}
                  personaConfig={path.personaConfig}
                  size="large"
                  pageId={view.pageId}
                  partials={view.partials}
                  clicks={view.clicks}
                  image={view.image}
                  width={view.width}
                  height={view.height}
                />
              </ViewContainer>
            </Tooltip>
          </StyledMainContent>
          {featurePreview ? <StyledSpacer /> : null}
        </Container>
        <Subtitle component="h3">{view.name || '-'}</Subtitle>
        <PathScreens path={path} views={path.views} />
      </ContentWrapper>
    </PageContent>
  );
};
